import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { API_BASE_URL, fetchJson } from './api';
import {
    AnalyticsMetricCard,
    AnalyticsPanelSection,
    analyticsBodyCellSx,
    analyticsHeadCellSx,
    analyticsTableSx,
    glassSubCardSx
} from './analyticsSurface';

function authHeaders() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

function startOfMonthIso() {
    const d = new Date();
    const s = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    return s.toISOString().slice(0, 10);
}

function formatDateTime(value) {
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value || '');
        return d.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return String(value || '');
    }
}

function clampCount(n) {
    const v = Number(n);
    return Number.isFinite(v) ? v : 0;
}

function buildQuery(filters, extras = {}) {
    const params = new URLSearchParams();
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.project_reference) params.set('project_reference', filters.project_reference);
    Object.entries(extras).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

function cellBg(count, max) {
    const c = clampCount(count);
    if (!max) return '#f8fafc';
    const t = Math.min(1, c / max);
    const light = 98 - Math.round(t * 20);
    return `hsl(215, 85%, ${light}%)`;
}

function normalizeCountSeries(rows, keyName) {
    return (Array.isArray(rows) ? rows : []).map((row) => ({
        name: String(row?.[keyName] ?? '').trim() || 'Unknown',
        count: clampCount(row?.count)
    }));
}

function scoreBandData(matrix) {
    const bands = new Map([
        ['Low', 0],
        ['Medium', 0],
        ['High', 0],
        ['Critical', 0]
    ]);
    for (const cell of Array.isArray(matrix) ? matrix : []) {
        const severity = Number(cell?.severity || 0);
        const likelihood = Number(cell?.likelihood || 0);
        const count = clampCount(cell?.count);
        const score = severity * likelihood;
        if (score >= 16) bands.set('Critical', (bands.get('Critical') || 0) + count);
        else if (score >= 9) bands.set('High', (bands.get('High') || 0) + count);
        else if (score >= 4) bands.set('Medium', (bands.get('Medium') || 0) + count);
        else bands.set('Low', (bands.get('Low') || 0) + count);
    }
    return Array.from(bands.entries()).map(([name, count]) => ({ name, count }));
}

const STATUS_COLORS = ['#2563eb', '#0f766e', '#f59e0b', '#dc2626'];
const CATEGORY_COLOR = '#2f80ed';
const SCORE_COLORS = ['#94a3b8', '#38bdf8', '#f59e0b', '#dc2626'];

function summaryCellBg(count, max) {
    const c = clampCount(count);
    if (!max) return '#f8fafc';
    const t = Math.min(1, c / max);
    const light = 98 - Math.round(t * 18);
    return `hsl(222, 85%, ${light}%)`;
}

function SimpleTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#ffffff', 0.72)}`, background: 'rgba(255,255,255,0.96)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.12)', p: 1.1 }}>
            {label ? <Typography sx={{ fontWeight: 800, fontSize: 12, mb: 0.35 }}>{label}</Typography> : null}
            {payload.map((entry) => (
                <Typography key={entry.dataKey} sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {entry.name || entry.dataKey}: <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{clampCount(entry.value)}</Box>
                </Typography>
            ))}
        </Box>
    );
}

export default function PortfolioRiskHeatmap({ roleMode = 'surveyor', onGoToReports }) {
    const requireProjectSelection = roleMode === 'developer';
    const [riskMode, setRiskMode] = useState('summary');

    const [fromDraft, setFromDraft] = useState('');
    const [toDraft, setToDraft] = useState('');
    const [statusDraft, setStatusDraft] = useState('Open');
    const [categoryDraft, setCategoryDraft] = useState('');
    const [projectDraft, setProjectDraft] = useState('');

    const [filters, setFilters] = useState({
        from: '',
        to: '',
        status: 'Open',
        category: '',
        project_reference: ''
    });

    const [categories, setCategories] = useState([]);
    const [projects, setProjects] = useState([]);
    const [matrix, setMatrix] = useState([]);
    const [totals, setTotals] = useState({ total_risks: 0, open_risks: 0 });
    const [summary, setSummary] = useState(null);
    const [heatmapLoading, setHeatmapLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [heatmapError, setHeatmapError] = useState('');
    const [summaryError, setSummaryError] = useState('');
    const [summaryRiskLoading, setSummaryRiskLoading] = useState(false);
    const [summaryRiskError, setSummaryRiskError] = useState('');
    const [summaryRiskMatrixData, setSummaryRiskMatrixData] = useState([]);
    const [summaryRiskHotspotsData, setSummaryRiskHotspotsData] = useState([]);
    const [summaryRiskProjectsRated, setSummaryRiskProjectsRated] = useState(0);

    const [selectedCell, setSelectedCell] = useState({ severity: null, likelihood: null });
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [tableLoading, setTableLoading] = useState(false);
    const [tableError, setTableError] = useState('');

    const baseQuery = useMemo(() => buildQuery(filters), [filters]);
    const listQuery = useMemo(() => buildQuery(filters, {
        severity: selectedCell.severity || '',
        likelihood: selectedCell.likelihood || '',
        page,
        page_size: pageSize,
        sort: 'updated_at_desc'
    }), [filters, page, pageSize, selectedCell]);
    const shouldPauseForProjectSelection = requireProjectSelection && !String(filters.project_reference || '').trim();

    useEffect(() => {
        if (shouldPauseForProjectSelection || riskMode !== 'summary') {
            setSummaryRiskMatrixData([]);
            setSummaryRiskHotspotsData([]);
            setSummaryRiskProjectsRated(0);
            setSummaryRiskError('');
            return;
        }

        let cancelled = false;
        (async () => {
            setSummaryRiskLoading(true);
            setSummaryRiskError('');
            try {
                const summaryQuery = buildQuery(filters, { active_only: '1' });
                const json = await fetchJson(`${API_BASE_URL}/api/portfolio/risks/summary-ratings${summaryQuery}`, {
                    method: 'GET',
                    headers: { ...authHeaders() }
                });

                if (cancelled) return;
                setSummaryRiskMatrixData(Array.isArray(json?.matrix) ? json.matrix : []);
                setSummaryRiskHotspotsData(Array.isArray(json?.hotspots) ? json.hotspots : []);
                setSummaryRiskProjectsRated(Number(json?.totals?.projects_rated || 0));
            } catch (e) {
                if (cancelled) return;
                setSummaryRiskMatrixData([]);
                setSummaryRiskHotspotsData([]);
                setSummaryRiskProjectsRated(0);
                setSummaryRiskError(String(e?.message || 'Failed to load summary risk ratings'));
            } finally {
                if (!cancelled) setSummaryRiskLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [filters, riskMode, shouldPauseForProjectSelection]);

    useEffect(() => {
        let cancelled = false;

        async function loadCatalog(nextFilters) {
            setCatalogLoading(true);
            try {
                const [categoryJson, projectJson] = await Promise.all([
                    fetchJson(`${API_BASE_URL}/api/portfolio/risks/categories${buildQuery(nextFilters)}`, { method: 'GET', headers: { ...authHeaders() } }),
                    fetchJson(`${API_BASE_URL}/api/portfolio/risks/projects${buildQuery(nextFilters)}`, { method: 'GET', headers: { ...authHeaders() } })
                ]);
                if (cancelled) return;
                const nextCategories = Array.isArray(categoryJson?.categories) ? categoryJson.categories : [];
                const nextProjects = Array.isArray(projectJson?.projects) ? projectJson.projects : [];
                setCategories(nextCategories);
                setProjects(nextProjects);

                if (requireProjectSelection && nextProjects.length === 1 && !String(nextFilters.project_reference || '').trim()) {
                    const onlyProject = nextProjects[0];
                    setProjectDraft(onlyProject);
                    setFilters((prev) => ({ ...prev, project_reference: onlyProject }));
                }
            } catch {
                if (cancelled) return;
                setCategories([]);
                setProjects([]);
            } finally {
                if (!cancelled) setCatalogLoading(false);
            }
        }

        loadCatalog(filters);
        return () => { cancelled = true; };
    }, [filters, requireProjectSelection]);

    useEffect(() => {
        if (shouldPauseForProjectSelection) {
            setMatrix([]);
            setRows([]);
            setTotal(0);
            setSummary(null);
            setTotals({ total_risks: 0, open_risks: 0 });
            setHeatmapError('');
            setSummaryError('');
            setTableError('');
            return;
        }

        let cancelled = false;
        setSelectedCell({ severity: null, likelihood: null });
        setPage(1);

        async function loadOverview() {
            setHeatmapLoading(true);
            setSummaryLoading(true);
            setHeatmapError('');
            setSummaryError('');
            try {
                const [heatmapJson, summaryJson] = await Promise.all([
                    fetchJson(`${API_BASE_URL}/api/portfolio/risks/heatmap${baseQuery}`, { method: 'GET', headers: { ...authHeaders() } }),
                    fetchJson(`${API_BASE_URL}/api/portfolio/risks/summary${baseQuery}`, { method: 'GET', headers: { ...authHeaders() } })
                ]);
                if (cancelled) return;
                setMatrix(Array.isArray(heatmapJson?.matrix) ? heatmapJson.matrix : []);
                setTotals(heatmapJson?.totals && typeof heatmapJson.totals === 'object' ? heatmapJson.totals : { total_risks: 0, open_risks: 0 });
                setSummary(summaryJson && typeof summaryJson === 'object' ? summaryJson : null);
            } catch (e) {
                if (cancelled) return;
                setMatrix([]);
                setTotals({ total_risks: 0, open_risks: 0 });
                setSummary(null);
                const msg = e?.message || 'Failed to load analytics summary';
                setHeatmapError(msg);
                setSummaryError(msg);
            } finally {
                if (!cancelled) {
                    setHeatmapLoading(false);
                    setSummaryLoading(false);
                }
            }
        }

        loadOverview();
        return () => { cancelled = true; };
    }, [baseQuery, shouldPauseForProjectSelection]);

    useEffect(() => {
        if (shouldPauseForProjectSelection) return;

        let cancelled = false;

        async function loadList() {
            setTableLoading(true);
            setTableError('');
            try {
                const res = await fetchJson(`${API_BASE_URL}/api/portfolio/risks${listQuery}`, {
                    method: 'GET',
                    headers: { ...authHeaders() }
                });
                if (cancelled) return;
                setRows(Array.isArray(res?.rows) ? res.rows : []);
                setTotal(Number(res?.total || 0));
            } catch (e) {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                setTableError(e?.message || 'Failed to load risks');
            } finally {
                if (!cancelled) setTableLoading(false);
            }
        }

        loadList();
        return () => { cancelled = true; };
    }, [listQuery, shouldPauseForProjectSelection]);

    function applyFilters() {
        setFilters({
            from: String(fromDraft || '').trim(),
            to: String(toDraft || '').trim(),
            status: String(statusDraft || 'Open').trim() || 'Open',
            category: String(categoryDraft || '').trim(),
            project_reference: String(projectDraft || '').trim()
        });
    }

    function resetFilters() {
        setFromDraft('');
        setToDraft('');
        setStatusDraft('Open');
        setCategoryDraft('');
        setProjectDraft('');
        setFilters({ from: '', to: '', status: 'Open', category: '', project_reference: '' });
    }

    const maxCount = useMemo(() => matrix.reduce((m, c) => Math.max(m, clampCount(c?.count)), 0), [matrix]);
    const gridCells = useMemo(() => {
        const byKey = new Map(matrix.map((c) => [`${c?.severity}:${c?.likelihood}`, c]));
        const out = [];
        for (let severity = 5; severity >= 1; severity -= 1) {
            for (let likelihood = 1; likelihood <= 5; likelihood += 1) {
                out.push(byKey.get(`${severity}:${likelihood}`) || { severity, likelihood, count: 0 });
            }
        }
        return out;
    }, [matrix]);

    const redZoneCount = useMemo(() => matrix
        .filter((cell) => Number(cell?.severity) >= 4 && Number(cell?.likelihood) >= 4)
        .reduce((sum, cell) => sum + clampCount(cell?.count), 0), [matrix]);

    const statusData = useMemo(() => normalizeCountSeries(summary?.status_counts, 'status'), [summary]);
    const severityData = useMemo(() => (Array.isArray(summary?.severity_counts) ? summary.severity_counts : []).map((row) => ({
        name: `S${Number(row?.severity || 0)}`,
        count: clampCount(row?.count)
    })), [summary]);
    const categoryData = useMemo(() => normalizeCountSeries(summary?.category_counts, 'category'), [summary]);
    const scoreData = useMemo(() => scoreBandData(matrix), [matrix]);
    const summaryRiskMatrix = useMemo(() => (
        (Array.isArray(summaryRiskMatrixData) ? summaryRiskMatrixData : []).map((row) => ({
            category: String(row?.category || '').trim() || 'Unknown',
            low: Number(row?.low || 0),
            medium: Number(row?.medium || 0),
            high: Number(row?.high || 0),
            total: Number(row?.total || 0),
            highSharePct: Number(row?.high_share_pct || 0)
        }))
    ), [summaryRiskMatrixData]);
    const summaryRiskMax = useMemo(() => {
        return summaryRiskMatrix.reduce((max, row) => Math.max(max, row.low, row.medium, row.high), 0);
    }, [summaryRiskMatrix]);
    const summaryHotspots = useMemo(() => {
        const fromApi = (Array.isArray(summaryRiskHotspotsData) ? summaryRiskHotspotsData : [])
            .map((row) => ({
                category: String(row?.category || '').trim() || 'Unknown',
                high: Number(row?.high || 0),
                highSharePct: Number(row?.high_share_pct || 0)
            }))
            .slice(0, 3);
        if (fromApi.length) return fromApi;
        return [...summaryRiskMatrix]
            .sort((a, b) => (b.high - a.high) || (b.highSharePct - a.highSharePct))
            .slice(0, 3);
    }, [summaryRiskHotspotsData, summaryRiskMatrix]);
    const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 25)));
    const roleScopeLabel = roleMode === 'developer'
        ? 'Single project scope'
        : roleMode === 'funder'
            ? 'Portfolio scope for accessible projects'
            : 'Cross-instruction risk scope';

    const selectedCellLabel = selectedCell.severity && selectedCell.likelihood
        ? `S${selectedCell.severity} / L${selectedCell.likelihood}`
        : 'All matrix cells';

    const kpiGrid = (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
            <AnalyticsMetricCard
                icon={<WarningAmberRoundedIcon fontSize="small" />}
                label="Open Risks"
                value={String(Number(summary?.totals?.open_risks ?? totals?.open_risks ?? 0))}
                caption="Open issues remaining in the current filtered scope."
                accent="linear-gradient(90deg, rgba(153, 27, 27, 0.92), rgba(244, 63, 94, 0.56))"
            />
            <AnalyticsMetricCard
                icon={<GridViewRoundedIcon fontSize="small" />}
                label="Red Zone"
                value={String(redZoneCount)}
                caption="Risks concentrated in the highest severity and likelihood zone."
                accent="linear-gradient(90deg, rgba(180, 83, 9, 0.9), rgba(245, 158, 11, 0.56))"
            />
            <AnalyticsMetricCard
                icon={<FolderOpenRoundedIcon fontSize="small" />}
                label="Projects in Scope"
                value={String(Number(summary?.totals?.project_count || 0))}
                caption={roleMode === 'developer' ? 'Projects you can select from for risk analysis.' : 'Projects contributing risk data inside the current filters.'}
                accent="linear-gradient(90deg, rgba(15, 118, 110, 0.9), rgba(45, 212, 191, 0.54))"
            />
            <AnalyticsMetricCard
                icon={<InsightsRoundedIcon fontSize="small" />}
                label="Total Risks"
                value={String(Number(summary?.totals?.total_risks ?? totals?.total_risks ?? 0))}
                caption={`${roleScopeLabel}. Status filter currently set to ${filters.status}.`}
                accent="linear-gradient(90deg, rgba(36, 50, 66, 0.95), rgba(59, 130, 246, 0.62))"
            />
        </Box>
    );

    return (
        <Box sx={{ display: 'grid', gap: 2.5, mt: 1 }}>
            {kpiGrid}

            <AnalyticsPanelSection
                eyebrow="Risk Controls"
                title="Filter and scope the risk view"
                subtitle="Apply time, status, category, and project filters before drilling into hotspots. Developers must select a project before analytics can render."
                right={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Analytics Mode</InputLabel>
                            <Select label="Analytics Mode" value={riskMode} onChange={(e) => setRiskMode(e.target.value)}>
                                <MenuItem value="summary">Summary Risk Ratings</MenuItem>
                                <MenuItem value="register">Risk Register Matrix</MenuItem>
                            </Select>
                        </FormControl>
                        <Chip size="small" label={roleScopeLabel} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9) }} />
                    </Stack>
                }
            >
                <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
                        <TextField label="From" type="date" value={fromDraft} onChange={(e) => setFromDraft(e.target.value)} InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: 160 }} />
                        <TextField label="To" type="date" value={toDraft} onChange={(e) => setToDraft(e.target.value)} InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: 160 }} />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="Mitigated">Mitigated</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Category</InputLabel>
                            <Select label="Category" value={categoryDraft} onChange={(e) => setCategoryDraft(e.target.value)}>
                                <MenuItem value="">All categories</MenuItem>
                                {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>{requireProjectSelection ? 'Project' : 'Project reference'}</InputLabel>
                            <Select label={requireProjectSelection ? 'Project' : 'Project reference'} value={projectDraft} onChange={(e) => setProjectDraft(e.target.value)}>
                                {!requireProjectSelection ? <MenuItem value="">All accessible projects</MenuItem> : null}
                                {projects.map((project) => <MenuItem key={project} value={project}>{project}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                        <Button variant="contained" onClick={applyFilters}>Apply filters</Button>
                        <Button variant="outlined" onClick={resetFilters}>Reset</Button>
                        <Button variant="text" onClick={() => { setFromDraft(startOfMonthIso()); setToDraft(todayIso()); }}>This month</Button>
                        <Chip size="small" label={catalogLoading ? 'Refreshing options…' : `${projects.length} project options`} sx={{ fontWeight: 800, backgroundColor: alpha('#ffffff', 0.55) }} />
                    </Stack>
                </Stack>
            </AnalyticsPanelSection>

            {shouldPauseForProjectSelection ? (
                <AnalyticsPanelSection
                    eyebrow="Project Required"
                    title="Select a project to unlock analytics"
                    subtitle="Developer analytics is intentionally single-project-first. Choose a project from the filter controls above, then apply the filters to load charts, the heatmap, and drilldown results for that project only."
                >
                    <Box sx={{ ...glassSubCardSx, maxWidth: 520 }}>
                        <Typography sx={{ fontWeight: 800, mb: 0.4 }}>Why this is gated</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            The developer view is scoped to one relevant project at a time so you can inspect risk detail without exposing unrelated instructions or portfolio-wide delivery data.
                        </Typography>
                    </Box>
                </AnalyticsPanelSection>
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.3fr 1fr' }, gap: 2.5 }}>
                        <AnalyticsPanelSection
                            eyebrow="Executive Overview"
                            title="Risk composition"
                            right={<Chip size="small" label={summaryLoading ? 'Refreshing…' : filters.status} sx={{ fontWeight: 800, backgroundColor: alpha('#e0f2fe', 0.9), color: '#075985' }} />}
                        >
                            {summaryError ? <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1 }}>{summaryError}</Typography> : null}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                                <Box sx={{ ...glassSubCardSx, minHeight: 280 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Status distribution</Typography>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie data={statusData} dataKey="count" nameKey="name" innerRadius={48} outerRadius={76} paddingAngle={2}>
                                                {statusData.map((entry, index) => <Cell key={`${entry.name}_${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip content={<SimpleTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Box sx={{ ...glassSubCardSx, minHeight: 280 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Severity profile</Typography>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={severityData}>
                                            <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="rgba(148, 163, 184, 0.28)" />
                                            <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                            <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                                            <Tooltip content={<SimpleTooltip />} />
                                            <Bar dataKey="count" name="Risks" fill="#2f80ed" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Box sx={{ ...glassSubCardSx, minHeight: 280 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Top categories</Typography>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={categoryData} layout="vertical" margin={{ left: 8, right: 8 }}>
                                            <CartesianGrid strokeDasharray="3 4" horizontal={false} stroke="rgba(148, 163, 184, 0.24)" />
                                            <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                                            <YAxis type="category" dataKey="name" width={110} tickLine={false} axisLine={false} />
                                            <Tooltip content={<SimpleTooltip />} />
                                            <Bar dataKey="count" name="Risks" fill={CATEGORY_COLOR} radius={[0, 8, 8, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Box sx={{ ...glassSubCardSx, minHeight: 280 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Score bands</Typography>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={scoreData}>
                                            <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="rgba(148, 163, 184, 0.28)" />
                                            <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                            <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                                            <Tooltip content={<SimpleTooltip />} />
                                            <Bar dataKey="count" name="Risks" radius={[8, 8, 0, 0]}>
                                                {scoreData.map((entry, index) => <Cell key={`${entry.name}_${index}`} fill={SCORE_COLORS[index % SCORE_COLORS.length]} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        </AnalyticsPanelSection>

                        <AnalyticsPanelSection
                            eyebrow={riskMode === 'summary' ? 'Summary Ratings Heatmap' : 'Heatmap'}
                            title={riskMode === 'summary' ? 'Category risk concentration' : 'Severity and likelihood hotspot matrix'}
                            subtitle={riskMode === 'summary'
                                ? 'This heatmap reflects summary project risk ratings across active project records (Low, Medium, High) for categories such as Programme and Costs.'
                                : 'Use the matrix to spot concentration quickly, then select a cell to focus the drilldown table below.'}
                            right={<Chip size="small" label={selectedCellLabel} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9) }} />}
                        >
                            {riskMode === 'summary' ? (
                                <Box sx={{ display: 'grid', gap: 1.25 }}>
                                    {summaryRiskError ? <Typography sx={{ color: '#b91c1c', fontSize: 13 }}>{summaryRiskError}</Typography> : null}
                                    {summaryRiskLoading ? <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Loading summary ratings...</Typography> : null}

                                    <Box sx={{ ...glassSubCardSx, p: 1.2 }}>
                                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Top high-risk categories</Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            {summaryHotspots.map((row) => (
                                                <Chip
                                                    key={row.category}
                                                    label={`${row.category}: ${row.high} High (${row.highSharePct}%)`}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            ))}
                                            {!summaryHotspots.length ? <Chip label="No summary ratings yet" /> : null}
                                        </Stack>
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Projects rated in current scope: <strong>{summaryRiskProjectsRated}</strong>
                                        </Typography>
                                    </Box>

                                    <TableContainer sx={{ borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.08)}`, backgroundColor: alpha('#ffffff', 0.56) }}>
                                        <Table sx={{ ...analyticsTableSx, minWidth: 620 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={analyticsHeadCellSx}>Category</TableCell>
                                                    <TableCell sx={{ ...analyticsHeadCellSx, textAlign: 'center' }}>Low</TableCell>
                                                    <TableCell sx={{ ...analyticsHeadCellSx, textAlign: 'center' }}>Medium</TableCell>
                                                    <TableCell sx={{ ...analyticsHeadCellSx, textAlign: 'center' }}>High</TableCell>
                                                    <TableCell sx={{ ...analyticsHeadCellSx, textAlign: 'center' }}>Projects Rated</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summaryRiskMatrix.map((row) => (
                                                    <TableRow key={row.category} hover>
                                                        <TableCell sx={{ ...analyticsBodyCellSx, fontWeight: 800 }}>{row.category}</TableCell>
                                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', backgroundColor: summaryCellBg(row.low, summaryRiskMax) }}>{row.low}</TableCell>
                                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', backgroundColor: summaryCellBg(row.medium, summaryRiskMax) }}>{row.medium}</TableCell>
                                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', backgroundColor: summaryCellBg(row.high, summaryRiskMax) }}>{row.high}</TableCell>
                                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', fontWeight: 800 }}>{row.total}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {!summaryRiskLoading && !summaryRiskMatrix.length ? (
                                                    <TableRow>
                                                        <TableCell sx={analyticsBodyCellSx} colSpan={5}>No summary risk ratings found for current scope.</TableCell>
                                                    </TableRow>
                                                ) : null}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ) : (
                                <>
                                    {heatmapError ? <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1 }}>{heatmapError}</Typography> : null}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr' }, gap: 2, alignItems: 'start' }}>
                                        <Box sx={glassSubCardSx}>
                                            <Typography sx={{ fontWeight: 800, mb: 1 }}>Axes</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}><strong>Y:</strong> Severity 5 to 1</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}><strong>X:</strong> Likelihood 1 to 5</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.2 }}><strong>Cell:</strong> {selectedCellLabel}</Typography>
                                        </Box>
                                        <Box>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1 }}>
                                                {gridCells.map((cell) => {
                                                    const severity = Number(cell?.severity || 0);
                                                    const likelihood = Number(cell?.likelihood || 0);
                                                    const count = clampCount(cell?.count);
                                                    const selected = selectedCell.severity === severity && selectedCell.likelihood === likelihood;
                                                    return (
                                                        <Box
                                                            key={`${severity}:${likelihood}`}
                                                            component="button"
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedCell({ severity, likelihood });
                                                                setPage(1);
                                                            }}
                                                            sx={{
                                                                minHeight: 86,
                                                                borderRadius: 3,
                                                                border: selected ? '2px solid #0f172a' : `1px solid ${alpha('#0f172a', 0.12)}`,
                                                                background: cellBg(count, maxCount),
                                                                cursor: 'pointer',
                                                                transition: 'transform 140ms ease, box-shadow 140ms ease',
                                                                boxShadow: selected ? '0 12px 24px rgba(15, 23, 42, 0.14)' : 'none',
                                                                '&:hover': { transform: 'translateY(-1px)' }
                                                            }}
                                                        >
                                                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                                                <Typography sx={{ fontWeight: 900, fontSize: 20, color: '#0f172a', lineHeight: 1 }}>{count}</Typography>
                                                                <Typography sx={{ fontSize: 11, color: alpha('#0f172a', 0.72), mt: 0.45 }}>S{severity} / L{likelihood}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                            {!heatmapLoading && !heatmapError && Number(totals?.open_risks || 0) === 0 ? (
                                                <Typography sx={{ mt: 1.2, color: 'text.secondary', fontSize: 13 }}>No risks found for the selected filters.</Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </AnalyticsPanelSection>
                    </Box>

                    <AnalyticsPanelSection
                        eyebrow="Drilldown"
                        title="Detailed risk register"
                        right={
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Chip size="small" label={total ? `${total} matching risks` : 'No matching risks'} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9) }} />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Page size</InputLabel>
                                    <Select label="Page size" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                        {[10, 25, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}/page</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Button variant="outlined" onClick={() => setSelectedCell({ severity: null, likelihood: null })}>Clear cell</Button>
                            </Stack>
                        }
                    >
                        {tableError ? <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1 }}>{tableError}</Typography> : null}
                        <TableContainer sx={{ borderRadius: 4, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.56) }}>
                            <Table sx={{ ...analyticsTableSx, minWidth: 920 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 140 }}>Project</TableCell>
                                        <TableCell sx={analyticsHeadCellSx}>Risk Title</TableCell>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 150 }}>Category</TableCell>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 92, textAlign: 'center' }}>Severity</TableCell>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 102, textAlign: 'center' }}>Likelihood</TableCell>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 178 }}>Last Updated</TableCell>
                                        <TableCell sx={{ ...analyticsHeadCellSx, width: 120 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(Array.isArray(rows) ? rows : []).map((row) => {
                                        const ref = String(row?.project_reference || '').trim() || '—';
                                        return (
                                            <TableRow key={String(row?.risk_id || `${ref}:${row?.title}`)} hover>
                                                <TableCell sx={{ ...analyticsBodyCellSx, fontWeight: 900 }}>
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        onClick={() => {
                                                            if (typeof onGoToReports === 'function') onGoToReports(ref);
                                                            else {
                                                                const qs = new URLSearchParams(window.location.search || '');
                                                                qs.set('view', 'reports');
                                                                window.location.search = qs.toString();
                                                            }
                                                        }}
                                                        sx={{ padding: 0, border: 0, background: 'transparent', color: '#2563eb', fontWeight: 900, cursor: 'pointer', textAlign: 'left' }}
                                                    >
                                                        {ref}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={analyticsBodyCellSx}>{String(row?.title || '').trim() || '—'}</TableCell>
                                                <TableCell sx={analyticsBodyCellSx}>{String(row?.category || '').trim() || '—'}</TableCell>
                                                <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', fontWeight: 900 }}>{Number(row?.severity || 0) || '—'}</TableCell>
                                                <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'center', fontWeight: 900 }}>{Number(row?.likelihood || 0) || '—'}</TableCell>
                                                <TableCell sx={analyticsBodyCellSx}>{formatDateTime(row?.updated_at) || '—'}</TableCell>
                                                <TableCell sx={analyticsBodyCellSx}>
                                                    <Chip
                                                        size="small"
                                                        label={String(row?.status || '').trim() || '—'}
                                                        sx={{
                                                            fontWeight: 800,
                                                            backgroundColor: String(row?.status || '').trim() === 'Open'
                                                                ? alpha('#fee2e2', 0.92)
                                                                : String(row?.status || '').trim() === 'Mitigated'
                                                                    ? alpha('#dcfce7', 0.92)
                                                                    : alpha('#fef3c7', 0.92)
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {!tableLoading && (!rows || rows.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ ...analyticsBodyCellSx, color: 'text.secondary' }}>No risks found.</TableCell>
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.2} sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Page {page} of {totalPages}</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Prev</Button>
                                <Button variant="outlined" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</Button>
                            </Stack>
                        </Stack>
                    </AnalyticsPanelSection>
                </>
            )}
        </Box>
    );
}